/**
 * The cash counter the operator is billing AT.
 *
 * WHY THIS EXISTS
 *
 * `GET /op-billing/billing-context` resolves the cash session from a
 * `counterId`, and with no counter it returns `counter: null` and
 * `cashSession: null` — which the workstation correctly renders as "No open
 * cash session at this counter — cash cannot be taken". The OP Billing
 * workstation never passed one, so that message was unconditional: cash could
 * never be taken from the New Bill screen, whatever the operator did in Finance.
 *
 * Persisted, and deliberately: a counter is a physical desk, so it changes at a
 * shift boundary, not per navigation. Making the operator re-pick it on every
 * page load would be re-asking a question whose answer is "the same desk I was
 * at ten seconds ago".
 *
 * Held separately from `useBranchStore` rather than folded into it because the
 * two have different lifetimes — a user may switch branch without a counter
 * existing at the new one, and a stale counter from another branch must not
 * silently follow them there. `BillingCounterSelector` clears it on mismatch.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface BillingCounterSummary {
  id: string;
  code: string;
  name: string;
  /** Kept so a counter belonging to another branch can be detected and dropped. */
  branchId: string | null;
}

interface BillingCounterState {
  currentCounter: BillingCounterSummary | null;
  setCounter: (counter: BillingCounterSummary | null) => void;
}

export const useBillingCounterStore = create<BillingCounterState>()(
  persist(
    (set) => ({
      currentCounter: null,
      setCounter: (counter) => set({ currentCounter: counter }),
    }),
    { name: 'nexao-billing-counter' },
  ),
);
